import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { CreditCard, Loader2, X } from "lucide-react";
import { useToast } from "../../hooks/useToast";
import { cn } from "../../lib/utils";
import { useMarketing } from "../../context/MarketingContext";
import {
  completeMarketingCreditsPurchase,
  createMarketingCreditsPaymentIntent,
} from "../../services/marketingCreditsApi";
import { Button } from "../ui/Button";
import { Input } from "../ui/input2";
import { formatApiErrors } from "../../services/marketingTemplatesApi";
import { getStripePublishableKey } from "../../lib/stripePublishableKey";

const env = process.env as Record<string, string | undefined>;

const EMAIL_COST_PER_RECIPIENT = 0.03;
const SMS_COST_PER_RECIPIENT = 0.1;
const MIN_RECHARGE_GBP = 10;

type DrawerProps = {
  isOpen: boolean;
  onClose: () => void;
};

const PUBLISHABLE_KEY = getStripePublishableKey() ?? "";
const STRIPE_CONNECT_ACCOUNT_ID =
  env.REACT_APP_STRIPE_CONNECT_ACCOUNT_ID ?? env.STRIPE_CONNECT_ACCOUNT_ID ?? "";
/** Set true only if PaymentIntent is created ON the connected account (backend uses stripe_account on create). */
const USE_CONNECTED_STRIPE_JS =
  env.REACT_APP_STRIPE_USE_CONNECTED_STRIPE_JS === "true" ||
  env.REACT_APP_STRIPE_USE_CONNECTED_STRIPE_JS === "1";

const cardElementOptions = {
  style: {
    base: {
      fontSize: "15px",
      color: "#1f2937",
      "::placeholder": {
        color: "#9ca3af",
      },
    },
    invalid: {
      color: "#dc2626",
    },
  },
};

const RechargeForm: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();
  const { remainingCredits, refreshCreditBalanceFromStorage } = useMarketing();

  const [amount, setAmount] = useState<string>("20");
  const [processing, setProcessing] = useState(false);
  const [cardError, setCardError] = useState<string>("");

  const numericAmount = Number(amount || 0);
  const emailEstimate = Math.floor((Number.isFinite(numericAmount) ? numericAmount : 0) / EMAIL_COST_PER_RECIPIENT);
  const smsEstimate = Math.floor((Number.isFinite(numericAmount) ? numericAmount : 0) / SMS_COST_PER_RECIPIENT);

  const submitRecharge = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!Number.isFinite(numericAmount) || numericAmount < MIN_RECHARGE_GBP) {
      toast.error(`Minimum recharge amount is £${MIN_RECHARGE_GBP}.`);
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      toast.error("Card field is not ready yet.");
      return;
    }

    try {
      setProcessing(true);
      setCardError("");

      const { client_secret } = await createMarketingCreditsPaymentIntent({
        amount: numericAmount,
        connect_account_id: STRIPE_CONNECT_ACCOUNT_ID || undefined,
      });

      const confirmResult = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (confirmResult.error) {
        setCardError(confirmResult.error.message || "Payment failed.");
        return;
      }

      const pi = confirmResult.paymentIntent;
      if (!pi?.id || pi.status !== "succeeded") {
        setCardError("Payment was not completed.");
        return;
      }

      const response = await completeMarketingCreditsPurchase({
        payment_intent_id: pi.id,
        connect_account_id: STRIPE_CONNECT_ACCOUNT_ID || undefined,
      });

      const nextBalanceRaw =
        response?.data?.company?.credit_balance ?? response?.data?.credit_balance;
      const nextBalance = Number(nextBalanceRaw);
      if (Number.isFinite(nextBalance)) {
        const currentRaw = localStorage.getItem("user");
        if (currentRaw) {
          const user = JSON.parse(currentRaw) as any;
          const nextUser = {
            ...user,
            company: {
              ...(user?.company || {}),
              credit_balance: nextBalance,
            },
          };
          localStorage.setItem("user", JSON.stringify(nextUser));
        }
      }
      refreshCreditBalanceFromStorage();
      toast.success(response?.message || "Credits recharged successfully.");
      onClose();
    } catch (err) {
      toast.error(formatApiErrors(err));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={submitRecharge} className="space-y-5">
      <div className="bg-purple-50 border border-purple-100 rounded-xl p-3">
        <p className="text-xs text-purple-700 font-medium">Current credit balance</p>
        <p className="text-2xl font-bold text-[#402B69] mt-1">{remainingCredits.toFixed(2)}</p>
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-gray-700">Recharge amount</label>
        <Input
          type="number"
          step="0.01"
          min={MIN_RECHARGE_GBP}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder={`e.g. ${MIN_RECHARGE_GBP}`}
        />
        <p className="text-xs text-gray-500">Minimum {MIN_RECHARGE_GBP} per recharge.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
          <p className="text-xs text-gray-600">Approx email sends</p>
          <p className="text-lg font-bold text-gray-900">{emailEstimate.toLocaleString()}</p>
          <p className="text-[11px] text-gray-500 mt-1">Based on 0.03 per email</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
          <p className="text-xs text-gray-600">Approx SMS sends</p>
          <p className="text-lg font-bold text-gray-900">{smsEstimate.toLocaleString()}</p>
          <p className="text-[11px] text-gray-500 mt-1">Based on 0.10 per SMS</p>
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-gray-700">Card details</label>
        <div className="rounded-xl border border-gray-200 bg-white px-3 py-3">
          <CardElement options={cardElementOptions} />
        </div>
        {cardError ? <p className="text-xs text-red-500">{cardError}</p> : null}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose} disabled={processing}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={processing || !stripe || !elements}
          className="bg-[#885ABB] text-white hover:bg-[#6f47a2]"
        >
          {processing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
              Processing
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4 mr-1.5" />
              Recharge Credits
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export const RechargeCreditsDrawer: React.FC<DrawerProps> = ({ isOpen, onClose }) => {
  const stripePromise = useMemo(
    () =>
      PUBLISHABLE_KEY
        ? loadStripe(
          PUBLISHABLE_KEY,
          USE_CONNECTED_STRIPE_JS && STRIPE_CONNECT_ACCOUNT_ID
            ? { stripeAccount: STRIPE_CONNECT_ACCOUNT_ID }
            : undefined,
        )
        : null,
    [],
  );

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[1199]"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      <div
        className={cn(
          "fixed top-0 right-0 h-full w-full max-w-xl bg-white shadow-2xl z-[1200] transform transition-transform duration-300 ease-in-out flex flex-col",
          isOpen ? "translate-x-0" : "translate-x-full",
        )}
      >
        {isOpen && (
          <button
            onClick={onClose}
            className="absolute lg:-left-14 top-4 lg:top-8 right-4 lg:right-auto w-10 h-10 flex items-center justify-center rounded-full bg-white text-[#885ABB] shadow-lg hover:bg-[#402B69] hover:text-white transition-all z-[110] group"
          >
            <X size={20} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
          </button>
        )}

        <div className="flex-1 flex flex-col overflow-y-auto bg-[#F9F9F9]">
          <div className="px-5 py-4 bg-white border-b border-gray-100">
            <h2 className="text-[17px] font-bold text-[#402B69]">Recharge Credits</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Add credits using Stripe card payment.
            </p>
          </div>

          <div className="p-5">
            {PUBLISHABLE_KEY && stripePromise ? (
              <Elements stripe={stripePromise}>
                <RechargeForm onClose={onClose} />
              </Elements>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

